import { useMemo, useState } from "react";
import { useProductItemApi } from "../../hooks/useProductItemApi.ts";
import { ProductItem } from "./types.ts";
import { DataGrid, GridActionsCellItem, GridColDef, GridDeleteIcon } from "@mui/x-data-grid";
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ConfirmDialog from "../estimates/ConfirmDialog.tsx";

type Row = {
  id: string;
  name: string;
  size: string;
  unit?: string;
  price?: number;
  raw: ProductItem;
};

interface ProductItemListProbs{
    data: ProductItem[];
    isLoading: boolean;
    onRefresh?: () => Promise<void> | void;
}

const fmtThousand = (v: unknown) => {
  const n = typeof v === 'number' ? v : Number(v);
  return Number.isFinite(n) ? n.toLocaleString('ko-KR') : '';
};

export default function ProductItemTable({
    data,
    isLoading,
    onRefresh
}: ProductItemListProbs) {

    const { deleteProductItem } = useProductItemApi();

    const rows: Row[] = useMemo(
        () =>
          data.map((e) => ({
            id: e.id,
            name: e.name,
            size: e.size,
            unit: e.unit,
            price: e.price,
            raw: e
          })),
        [data]
      );

    const [confirmOpen, setConfirmOpen] = useState(false);
    const [targetId, setTargetId] = useState<string | null>(null);
    const [submitting, setSubmitting] = useState(false);

    const [expandedId, setExpandedId] = useState<string | null>(null);

    const askDelete = (id: string) => {
        setTargetId(id);
        setConfirmOpen(true);
    };

    const handleConfirm = async () => {
        if (!targetId) return;
        try {
        setSubmitting(true);
        await deleteProductItem(targetId);
        setConfirmOpen(false);
        setTargetId(null);
        if (onRefresh) await onRefresh();
        else window.location.reload();
        } catch (e: any) {
        alert(e.message ?? "삭제 중 오류");
        } finally {
        setSubmitting(false);
        }
    };

    const [detailOpen, setDetailOpen] = useState(false);
    const [editing, setEditing] = useState<ProductItem | null>(null);

    const openDetail = (dto: ProductItem) => { setEditing(dto); setDetailOpen(true); };
    const closeDetail = () => { setDetailOpen(false); setEditing(null); };

    const columns: GridColDef[] = [
        { field: "name", headerName: "제품", flex: 1, }, 
        { field: "size", headerName: "규격", flex: 1, }, 
        { field: "unit", headerName: "단위", flex: 1, },  
        {
        field: 'price',
        headerName: '가격',
        flex: 1,
        type: 'number',            // 정렬을 숫자로
        align: 'right',
        headerAlign: 'right',
        renderCell: ({ value }) => <>{fmtThousand(value)}</>,
        // valueFormatter: ({ value }) => fmtThousand(value),
        },
        {
            field: "actions",
            type: "actions",
            flex: 1,
            getActions: (params) => [
                <GridActionsCellItem
                    key="delete"
                    icon={<GridDeleteIcon />}
                    label="Delete"
                    onClick={() => askDelete(String(params.id))}
                />
                ]
        },
    ];

    return (
        <>
            <DataGrid
                rows={rows}
                columns={columns}
                getRowId={(r) => r.id}
                loading={isLoading && rows.length === 0}
                autoHeight
                pageSizeOptions={[20]}
                initialState={{
                    pagination: { paginationModel: { pageSize: 20, page: 0 } },
                    sorting: { sortModel: [{ field: 'name', sort: 'asc' }] },
                }}
                disableRowSelectionOnClick
            />

            <ConfirmDialog
                open={confirmOpen}
                title="정말 지우시겠습니까?"
                message="이 작업은 되돌릴 수 없습니다."
                onClose={() => setConfirmOpen(false)}
                onConfirm={handleConfirm}
                loading={submitting}
                confirmText="예"
                cancelText="아니오"
            />
        </>
    )


}